<template>
  <section>
    <div v-if="$apollo.queries.competitionEntryRules.loading">
      <v-progress-circular indeterminate color="black" />
      &nbsp;{{ $t("common:loading.messsage", "Loading...") }}
    </div>
    <div v-else>
      <v-row no-gutters>
        <v-col cols="9">
          <h1 style="font-size: 16px">
            {{ $t("events:common.entryRules.heading", "Entry rules") }}
          </h1>
        </v-col>
        <v-col cols="3" class="text-end">
          <v-btn-icon
            v-if="
              $allowedMutation(
                competition.allowedMutations,
                'competitionEntryRuleCreateOrUpdate'
              )
            "
            :to="{
              name: 'CompetitionRulesFormNew',
              params: {
                competitionId: competition.id
              }
            }"
            :aria-label="$t('common:newButton.alt')"
            size="small"
            icon
            data-test="newEntryRule"
            class="bg-edit"
          >
            <v-icon class="text-white" size="small"> $iconPlus </v-icon>
          </v-btn-icon>
        </v-col>
      </v-row>

      <v-col cols="12" class="px-0">
        <div
          v-for="(rule, index) in publicRegistrationRulesDescriptions"
          :key="index"
          data-test="validationRule"
          class="mb-1"
        >
          {{ displayText(rule) }}
        </div>
      </v-col>

      <div v-if="sectionsWithDescriptions.length">
        <div
          v-for="section in sectionsWithDescriptions"
          :key="section.id"
          data-test="sections"
          class="mb-1"
        >
          <span class="text-secondary">{{ section.name }}:</span>
          {{ section.description }}
        </div>
      </div>

      <div
        v-if="competitionEntryRules && competitionEntryRules.length"
        data-test="entryRules"
      >
        {{
          $t(
            "events:common.entryRules.hint",
            "and to enter this class, athlete/horse combination should comply to atleast one of the following sets of rules: "
          )
        }}

        <br /><br />

        <CompetitionEntryRuleSet
          v-for="(entryRule, index) in competitionEntryRules"
          :key="entryRule.id"
          :competition="competition"
          :entry-rule="entryRule"
          :rule-number="
            competitionEntryRules.length > 1 ? index + 1 : undefined
          "
          class="v-col-12 v-col-md-5 mb-10"
        />
      </div>
    </div>
  </section>
</template>
<script>
import { defineComponent, computed } from "vue";
import gql from "fraql";
import {
  useDisplayText,
  DisplayTextFragments
} from "@composables/display_text";

import CompetitionEntryRuleSet from "./CompetitionEntryRuleSet.vue";

export default defineComponent({
  name: "CompetitionEntryRules",
  components: {
    CompetitionEntryRuleSet
  },
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { displayText } = useDisplayText();
    const sectionsWithDescriptions = computed(() => {
      return (props.competition.sections ?? []).filter(x => !!x.description);
    });
    return {
      displayText,
      sectionsWithDescriptions
    };
  },
  data() {
    return {
      publicRegistrationRulesDescriptions: [],
      competitionEntryRules: []
    };
  },
  apollo: {
    competitionEntryRules: {
      query: gql`
        query competitionEntryRules($competitionId: ID) {
          competition(id: $competitionId) {
            id
            publicRegistrationRulesDescriptions {
              ${DisplayTextFragments.translation}
            }
            competitionEntryRules {
              id
              ${CompetitionEntryRuleSet.fragments.competitionEntryRuleFields}
              allowedMutations
            }
          }
        }
      `,
      variables() {
        return {
          competitionId: this.competition.id
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading || !data) return;
        this.publicRegistrationRulesDescriptions =
          data.competition.publicRegistrationRulesDescriptions;
        this.competitionEntryRules = data.competition.competitionEntryRules;
      }
    }
  },
  fragments: {
    competitionFields: gql`
      fragment _ on Competition {
        id
        sectionIds
        sections @client {
          id
          name
          nameTranslations {
            locale
            value
          }
          description
          descriptionTranslations {
            locale
            value
          }
        }
        allowedMutations
      }
    `
  }
});
</script>
